import React, { useEffect, useRef, useState } from "react"
import { SpinnerText } from "../../ui/Spinner"
import { checkTerms } from "../../../services/euipoApi"
import { classDescriptions } from "../../../utils/classDescriptions"
import ClassBox from "../../ui/ClassBox"
import InfoBox from "../../ui/InfoBox"

type Entry = { conceptId?: string; classNumber?: number; text: string }
type PendingTerm = { classNumber: number; text: string }
type TermStatus = boolean | null | "checking" | "error"

function termKey(classNumber: number, text: string) {
  return `${classNumber}|${text.trim().toLocaleLowerCase()}`
}

export default function ManualList({
  terms,
  setTerms
}: {
  terms: Entry[]
  setTerms: React.Dispatch<React.SetStateAction<Entry[]>>
}) {
  const [selectedNumber, setSelectedNumber] = useState<number | null>(null)
  const [inputValue, setInputValue] = useState("")
  const [pendingTerms, setPendingTerms] = useState<PendingTerm[]>([])
  const [termStatuses, setTermStatuses] = useState<Record<string, TermStatus>>({})
  const requestedTerms = useRef(new Set<string>())
  const completedTerms = pendingTerms
  const hasPendingChecks = completedTerms.some(
    ({ classNumber, text }) =>
      termStatuses[termKey(classNumber, text)] === "checking"
  )
  const hasFailedChecks = completedTerms.some(
    ({ classNumber, text }) =>
      termStatuses[termKey(classNumber, text)] === "error"
  )

  useEffect(() => {
    const newTerms = pendingTerms.filter(({ classNumber, text }) => {
      const key = termKey(classNumber, text)
      if (requestedTerms.current.has(key)) return false
      requestedTerms.current.add(key)
      return true
    })
    if (newTerms.length === 0) return

    setTermStatuses(previous => ({
      ...previous,
      ...Object.fromEntries(
        newTerms.map(({ classNumber, text }) => [
          termKey(classNumber, text),
          "checking"
        ])
      )
    }))

    const groupedTerms = new Map<number, string[]>()
    for (const { classNumber, text } of newTerms) {
      groupedTerms.set(classNumber, [
        ...(groupedTerms.get(classNumber) ?? []),
        text
      ])
    }
    const classes = Array.from(groupedTerms, ([classNumber, classTerms]) => ({
      classNumber,
      terms: classTerms
    }))

    checkTerms(classes)
      .then(result => {
        const responseTerms = new Map<string, boolean | null>()
        for (const group of result.goodsAndServices ?? []) {
          for (const term of group.terms ?? []) {
            responseTerms.set(
              termKey(group.classNumber, term.text),
              term.harmonized ?? null
            )
          }
        }

        setTermStatuses(previous => ({
          ...previous,
          ...Object.fromEntries(
            newTerms.map(({ classNumber, text }) => {
              const key = termKey(classNumber, text)
              return [key, responseTerms.get(key) ?? null]
            })
          )
        }))
      })
      .catch((error: unknown) => {
        console.error("Fehler bei der EUIPO-Validierung manueller Begriffe:", error)
        setTermStatuses(previous => ({
          ...previous,
          ...Object.fromEntries(
            newTerms.map(({ classNumber, text }) => [
              termKey(classNumber, text),
              "error"
            ])
          )
        }))
      })
  }, [pendingTerms])

  function addPendingTerms(texts: string[], classNumber: number) {
    const existing = new Set(
      pendingTerms
        .filter(term => term.classNumber === classNumber)
        .map(term => term.text.toLocaleLowerCase())
    )
    const additions = texts
      .map(text => text.trim())
      .filter(Boolean)
      .filter(text => {
        const normalized = text.toLocaleLowerCase()
        if (existing.has(normalized)) return false
        existing.add(normalized)
        return true
      })
      .map(text => ({ classNumber, text }))

    if (additions.length > 0) {
      setPendingTerms(previous => [...previous, ...additions])
    }
  }

  function handleInputChange(value: string) {
    if (selectedNumber == null || !value.includes(";")) {
      setInputValue(value)
      return
    }

    const segments = value.split(";")
    addPendingTerms(segments.slice(0, -1), selectedNumber)
    setInputValue(segments[segments.length - 1])
  }

  function handleAddTerms() {
    if (selectedNumber == null) return

    const additions = [
      ...pendingTerms,
      ...(inputValue.trim()
        ? [{ classNumber: selectedNumber, text: inputValue.trim() }]
        : [])
    ]
    if (additions.length === 0) return

    setTerms(previous => {
      const existing = new Set(
        previous.map(
          term =>
            `${term.classNumber}|${term.text.toLocaleLowerCase()}`
        )
      )
      const updated = [...previous]
      for (const term of additions) {
        const key = `${term.classNumber}|${term.text.toLocaleLowerCase()}`
        if (existing.has(key)) continue
        updated.push(term)
        existing.add(key)
      }
      sessionStorage.setItem("checkedTerms", JSON.stringify(updated))
      return updated
    })
    setPendingTerms([])
    setInputValue("")
  }

  function handleClear() {
    if (window.confirm("Clear your directory?")) {
      setTerms([])
      setPendingTerms([])
      setInputValue("")
      setTermStatuses({})
      requestedTerms.current.clear()
      sessionStorage.removeItem("checkedTerms")
    }
  }

  function removePendingTerm(term: PendingTerm) {
    const key = termKey(term.classNumber, term.text)
    setPendingTerms(previous =>
      previous.filter(
        item => termKey(item.classNumber, item.text) !== key
      )
    )
    requestedTerms.current.delete(key)
    setTermStatuses(previous => {
      const next = { ...previous }
      delete next[key]
      return next
    })
  }

  const uniqueClasses = new Set(
    terms.map(term => term.classNumber).filter(number => number != null)
  ).size

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-4 py-3 text-sm">
        <div className="flex gap-5 text-slate-700">
          <span><strong className="text-blue-800">{uniqueClasses}</strong> classes</span>
          <span><strong className="text-blue-800">{terms.length}</strong> terms</span>
        </div>
        <button
          type="button"
          onClick={handleClear}
          disabled={!terms.length}
          className="text-sm font-medium text-slate-600 hover:text-red-600 disabled:opacity-40"
        >
          Clear directory
        </button>
      </div>
      <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
        <strong>Note:</strong> It is recommended to use terms from the Harmonized Database and keep the list as short as possible.
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-1 text-sm font-semibold text-blue-900">Select a Nice class</h3>
          <p className="mb-3 text-xs text-slate-500">Choose the class your terms belong to.</p>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(42px,1fr))] gap-2">
            {Array.from({ length: 45 }, (_, index) => (
              <ClassBox
                key={index + 1}
                number={index + 1}
                selected={selectedNumber === index + 1}
                onClick={() => setSelectedNumber(index + 1)}
              />
            ))}
          </div>
          {selectedNumber && (
            <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm">
              <p className="mb-1 font-semibold text-blue-900">Class {selectedNumber}</p>
              <p className="text-slate-600">{classDescriptions[selectedNumber]}</p>
            </div>
          )}
        </div>
        <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-2 flex items-center gap-2">
            <label htmlFor="manual-goods-terms" className="text-sm font-semibold text-blue-900">Add your terms</label>
            <InfoBox title="Info">Separate terms with semicolons (e.g. milk; cheese; butter;).</InfoBox>
          </div>
          <p className="mb-3 text-xs text-slate-500">Separate individual terms with a semicolon.</p>
          <div className="flex min-h-44 flex-wrap content-start items-start gap-2 rounded-lg border border-slate-300 p-3 focus-within:border-blue-600 focus-within:ring-2 focus-within:ring-blue-100">
            {pendingTerms.map(term => {
              const status = termStatuses[termKey(term.classNumber, term.text)]
              const style =
                status === true
                  ? "border-green-300 bg-green-50"
                  : status === false
                    ? "border-orange-300 bg-orange-50"
                    : status === "error"
                      ? "border-red-300 bg-red-50"
                      : "border-slate-200 bg-white"

              return (
                <span
                  key={termKey(term.classNumber, term.text)}
                  role="group"
                  aria-label={`Term ${term.text}`}
                  className={`inline-flex max-w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm text-slate-800 ${style}`}
                >
                  <span className="break-words">{term.text}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${term.text}`}
                    title="Remove term"
                    onClick={() => removePendingTerm(term)}
                    className="shrink-0 font-bold text-slate-500 hover:text-red-600"
                  >
                    ×
                  </button>
                </span>
              )
            })}
            <input
              id="manual-goods-terms"
              aria-label="Add your terms"
              value={inputValue}
              onChange={event => handleInputChange(event.target.value)}
              onKeyDown={event => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault()
                  handleAddTerms()
                }
              }}
              placeholder={
                selectedNumber == null
                  ? "Select a class first"
                  : "e.g. milk; dairy products; cheese;"
              }
              disabled={selectedNumber == null}
              className="min-w-[8rem] flex-1 border-0 bg-transparent p-1 text-sm outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
            />
          </div>
          {hasPendingChecks && (
            <p role="status" className="mt-2 text-xs text-slate-500">
              <SpinnerText>Checking completed terms against the Harmonized Database…</SpinnerText>
            </p>
          )}
          {hasFailedChecks && (
            <p role="alert" className="mt-2 text-xs text-red-600">
              Could not check one or more terms against the Harmonized Database.
            </p>
          )}
          <button
            type="button"
            onClick={handleAddTerms}
            disabled={!selectedNumber || (!pendingTerms.length && !inputValue.trim())}
            className="mt-3 w-full rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            Add terms
          </button>
        </div>
      </div>
    </div>
  )
}
